import { cognitiveIndices } from './eegIndices';
import { derivePhases, type PhaseSpan, type TraitFrame } from './buildTimeline';
import { readLines } from './readLines';
import { TailScope } from './scope';
import { rawToPercent, traitRaw } from './traitScoring';
import type { SensorSample, SessionManifest } from './types';

/**
 * The split dialog's timeline — duration, phases and the EEG focus trace —
 * read from the recording as a STREAM.
 *
 * The replay path (`parseNdjson` + `buildTimeline`) holds the whole file as
 * one string and every sample as an object. A two-hour recording is over
 * 1 GB of NDJSON, past the browser's longest possible string, so the split
 * dialog came up with a 0:00 timeline and could not split at all. This
 * reads the body chunk by chunk, keeps only what the dialog draws, and uses
 * the same time base as `buildTimeline` and the server's split
 * (`t0 = min(hostTime)`, per-sensor re-anchoring when clocks are skewed), so
 * a cut lands where the operator put it.
 */

const EEG_KEYS = ['eeg:TP9', 'eeg:AF7', 'eeg:AF8', 'eeg:TP10'];
const TRAIT_TICK_MS = 1000; // matches buildTimeline / the live trait bars
const WINDOW = 256; // the longest window cognitiveIndices reads
/** The chart gets at most this many points (bucket means beyond it). */
export const MAX_TRACE_POINTS = 1500;

export interface SplitTimeline {
  durationS: number;
  phases: PhaseSpan[];
  traits: TraitFrame[];
}

/** A growable Float64Array. */
class Column {
  data = new Float64Array(1024);
  length = 0;

  push(v: number): void {
    if (this.length === this.data.length) {
      const next = new Float64Array(this.data.length * 2);
      next.set(this.data);
      this.data = next;
    }
    this.data[this.length++] = v;
  }
}

export class SplitTimelineBuilder {
  private manifest: SessionManifest | null = null;
  private readonly minBy = new Map<string, number>();
  private readonly maxBy = new Map<string, number>();
  // EEG lines, compact: host time, sensor, channel, and where their values
  // sit in `eegValues`.
  private readonly eegTime = new Column();
  private readonly eegSensor: string[] = [];
  private readonly eegChannel: number[] = [];
  private readonly eegStart: number[] = [];
  private readonly eegValues = new Column();
  // Lines where the phase tag changes (the fallback when the manifest has
  // no phase segments).
  private readonly phaseMarks: SensorSample[] = [];
  private lastPhase: string | undefined = undefined;
  private sawSample = false;

  /** Feed one NDJSON line; malformed lines are skipped, as in parseNdjson. */
  feedLine(raw: string): void {
    const line = raw.trim();
    if (line === '') return;
    let rec: Record<string, unknown>;
    try {
      const obj: unknown = JSON.parse(line);
      if (typeof obj !== 'object' || obj === null) return;
      rec = obj as Record<string, unknown>;
    } catch {
      return;
    }
    if (rec.kind === 'session') {
      this.manifest = rec as unknown as SessionManifest;
      return;
    }
    if (
      rec.kind !== 'sample' ||
      typeof rec.sensorId !== 'string' ||
      typeof rec.stream !== 'string' ||
      typeof rec.hostTime !== 'number' ||
      !Array.isArray(rec.values)
    ) {
      return;
    }
    const sensorId = rec.sensorId;
    const t = rec.hostTime;
    const mn = this.minBy.get(sensorId);
    if (mn === undefined || t < mn) this.minBy.set(sensorId, t);
    const mx = this.maxBy.get(sensorId);
    if (mx === undefined || t > mx) this.maxBy.set(sensorId, t);

    const phase = typeof rec.phase === 'string' ? rec.phase : undefined;
    if (!this.sawSample || phase !== this.lastPhase) {
      this.phaseMarks.push({
        sensorId,
        stream: rec.stream,
        hostTime: t,
        deviceTime: null,
        values: [],
        phase,
      });
      this.lastPhase = phase;
    }
    this.sawSample = true;

    const ch = EEG_KEYS.indexOf(rec.stream);
    if (ch < 0) return;
    this.eegTime.push(t);
    this.eegSensor.push(sensorId);
    this.eegChannel.push(ch);
    this.eegStart.push(this.eegValues.length);
    for (const v of rec.values as unknown[]) {
      if (typeof v === 'number' && Number.isFinite(v)) this.eegValues.push(v);
    }
  }

  finish(): SplitTimeline {
    if (this.minBy.size === 0) return { durationS: 0, phases: [], traits: [] };
    // Same skew rule as buildTimeline.realignSensorClocks and the server's
    // split: the union span dwarfing the widest sensor's own span means the
    // sensors are on different clock bases, so each starts at its own zero.
    let gMin = Infinity;
    let gMax = -Infinity;
    let widest = 0;
    for (const [id, mn] of this.minBy) {
      const mx = this.maxBy.get(id) ?? mn;
      gMin = Math.min(gMin, mn);
      gMax = Math.max(gMax, mx);
      widest = Math.max(widest, mx - mn);
    }
    const skewed = this.minBy.size > 1 && gMax - gMin > widest + 5000;
    const base = (sensorId: string) =>
      skewed ? (this.minBy.get(sensorId) ?? 0) : 0;
    const t0 = skewed ? 0 : gMin;
    const tEnd = skewed ? widest : gMax;
    const durationS = Math.max(0, (tEnd - t0) / 1000);

    const marks = this.phaseMarks
      .map((s) => ({ ...s, hostTime: s.hostTime - base(s.sensorId) }))
      .sort((a, b) => a.hostTime - b.hostTime);
    const phases = derivePhases(this.manifest, marks, t0, durationS);

    return { durationS, phases, traits: this.focusTrace(base, t0, tEnd) };
  }

  /** buildTimeline's 1 s trait tick, EEG only (the dialog plots focus). */
  private focusTrace(
    base: (sensorId: string) => number,
    t0: number,
    tEnd: number,
  ): TraitFrame[] {
    const n = this.eegTime.length;
    if (n === 0) return [];
    const times = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      times[i] = this.eegTime.data[i] - base(this.eegSensor[i]);
    }
    const order = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => times[a] - times[b] || a - b,
    );
    const scope = new TailScope(WINDOW);
    const frames: TraitFrame[] = [];
    const emit = (atMs: number) => {
      const cog = cognitiveIndices(scope, EEG_KEYS);
      const raw = traitRaw('focus', cog, {});
      frames.push({
        t: (atMs - t0) / 1000,
        focus: raw === null ? null : rawToPercent('focus', raw),
        stress: null,
        steady: null,
        tremor: null,
        fatigue: null,
        smoothness: null,
      });
    };
    let next = t0 + TRAIT_TICK_MS;
    const values = this.eegValues.data;
    for (const i of order) {
      while (times[i] >= next) {
        emit(next);
        next += TRAIT_TICK_MS;
      }
      const channel = EEG_KEYS[this.eegChannel[i]];
      const end = i + 1 < n ? this.eegStart[i + 1] : this.eegValues.length;
      for (let k = this.eegStart[i]; k < end; k++) scope.push(channel, values[k]);
    }
    emit(tEnd);
    return downsample(frames, MAX_TRACE_POINTS);
  }
}

/** Mean of each bucket, so a three-hour trace still draws quickly. */
export function downsample(frames: TraitFrame[], max: number): TraitFrame[] {
  if (frames.length <= max) return frames;
  const size = frames.length / max;
  const out: TraitFrame[] = [];
  for (let b = 0; b < max; b++) {
    const from = Math.floor(b * size);
    const to = Math.min(frames.length, Math.floor((b + 1) * size));
    let sum = 0;
    let count = 0;
    for (let i = from; i < to; i++) {
      const f = frames[i].focus;
      if (f !== null) {
        sum += f;
        count++;
      }
    }
    out.push({
      ...frames[Math.floor((from + to - 1) / 2)],
      focus: count > 0 ? sum / count : null,
    });
  }
  return out;
}

/**
 * Read a recording response as a stream into the split timeline.
 * `onProgress` gets the bytes read so far (decompressed — the browser
 * inflates a gzip-encoded recording before the body reaches us).
 */
export async function loadSplitTimeline(
  resp: Response,
  onProgress?: (bytes: number) => void,
  isCancelled: () => boolean = () => false,
): Promise<SplitTimeline | null> {
  const builder = new SplitTimelineBuilder();
  const finished = await readLines(resp, (l) => builder.feedLine(l), onProgress, isCancelled);
  return finished ? builder.finish() : null;
}
